import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Subject, timer, of } from 'rxjs';
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators';

import { Cliente_Perfil } from 'app/services/cliente/cliente_perfil.service'; // <-- o Cliente_Certifica

export interface TimbreResumenTipoDto {
  tipo: string;
  disponibles: number;
  consumidos: number;
}

export interface TimbresResumenDto {
  tipos: TimbreResumenTipoDto[];
  totalDisponibles: number;
}

@Component({
  selector: 'app-timbres-badge',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatMenuModule, MatProgressBarModule],
  templateUrl: './timbres-badge.component.html'
})
export class TimbresBadgeComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  isLoading = false;
  resumen: any | null = null;
  lastError: string | null = null;

  // Umbral para “saldo bajo”
  lowThreshold = 20;

  constructor(private clientePerfil: Cliente_Perfil) {} // <-- o Cliente_Certifica

  ngOnInit(): void {
    timer(0, 60_000)
      .pipe(
        tap(() => {
          this.isLoading = true;
          this.lastError = null;
        }),
        switchMap(() =>
          this.clientePerfil.GetTimbresResumen().pipe(
            catchError((err) => {
              this.lastError = err?.error?.message ?? 'No se pudo obtener el saldo.';
              return of(null);
            })
          )
        ),
        tap(() => (this.isLoading = false)),
        takeUntil(this.destroy$)
      )
      .subscribe((r) => (this.resumen = r));
  }

  refresh(): void {
    this.isLoading = true;
    this.lastError = null;

    this.clientePerfil.GetTimbresResumen()
      .pipe(
        catchError((err) => {
          this.lastError = err?.error?.message ?? 'No se pudo obtener el saldo.';
          return of(null);
        })
      )
      .subscribe((r) => {
        this.resumen = r;
        this.isLoading = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Badge
  get totalDisponibles(): number {
    return this.resumen?.totalDisponibles ?? 0;
  }

  get none(): boolean {
    return this.totalDisponibles <= 0;
  }

  get low(): boolean {
    return this.totalDisponibles > 0 && this.totalDisponibles <= this.lowThreshold;
  }

  // Helpers UI
  trackByTipo = (_: number, item: TimbreResumenTipoDto) => item.tipo;

  // Si quieres ordenar por disponibles desc:
  get tiposOrdenados(): TimbreResumenTipoDto[] {
    const tipos = this.resumen?.tipos ?? [];
    return [...tipos].sort((a, b) => (b.disponibles ?? 0) - (a.disponibles ?? 0));
  }

  tipoLabel(tipo: string): string {
    // Ajusta a tus nombres reales de enum/strings
    const t = (tipo || '').toLowerCase();
    if (t.includes('xml')) return 'Validación XML';
    if (t.includes('nomina')) return 'Nómina';
    if (t.includes('cfdi') || t.includes('timbr')) return 'Timbrado CFDI';
    return tipo;
  }
}